import { Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useImmer } from 'use-immer'
import { useTranslation } from 'react-i18next'
import React from 'react'
import _ from 'lodash'

import { Provider as ErrorsStateContextProvider } from '@/hooks/ErrorsStateContext'
import { Provider as InputStateContextProvider } from '@/hooks/inputStateContext'
import { useMerchant } from '@/hooks/merchantContext'

import { actions, useDispatch } from '@/redux'
import Dialog from '@/components/Dialog'

import AccountSettings from './settings/AccountSettings'
import BasicSettings from './settings/MerchantSettings'
import DatamartSettings from './settings/DatamartSettings'
import DirectorySettings from './settings/DirectorySettings'
import InvoiceSettings from './settings/InvoiceSettings'
import OrderSettings from './settings/OrderSettings'
import PaymentSettings from './settings/PaymentSettings'
import PrinterSettings from './settings/PrinterSettings'
import ReportSettings from './settings/ReportSettings'

const initialState = {}

// TH Tax Invoice 文字欄位（Owner Name / Branch No.）允許字元（FSD §8）：泰文 + 英文 + 數字 + 鍵盤符號。
// 即可印 ASCII（U+0020~U+007E：英文/數字/符號/空格）+ 泰文（U+0E00~U+0E7F）；中文、emoji、其他語系不允許。
const TH_TEXT_FIELD_PATTERN = /^[ -~฀-๿]+$/

/**
 *
 * @param {*} props
 * @returns
 */
export default function MerchantDialog (props) {
  const dispatch = useDispatch()
  const classes = useStyles(props)
  const { t } = useTranslation()
  const [errors, setErrors] = React.useState({})
  const [merchantState, setMerchant] = useMerchant()
  const [inputState, setInputState] = useImmer({})
  const updateInputState = (path, value) => {
    setInputState((draft) => {
      _.set(draft, path, value)
    })
  }

  React.useEffect(() => {
    setInputState((draft) => {
      // 初始 inputState
      const newState = {
        ...initialState,
        ...merchantState.merchant?.setting,
        // 經緯度特殊處理
        geometry: merchantState?.merchant?.setting?.location ? `${merchantState?.merchant?.setting?.location[1]},${merchantState?.merchant?.setting?.location[0]}` : '',
        // 帳號密碼特殊處理
        account: {
          username: merchantState?.merchant?.shortCode,
          password: undefined,
        },
      }
      return newState
    })
  }, [merchantState.merchant])

  const open = merchantState.openEditDialog

  const handleClose = () => {
    setMerchant('merchant', {})
    setMerchant('openEditDialog', false)
    setErrors({})
  }

  const handleSubmit = async () => {
    const params = { ...inputState }

    const region = merchantState.merchant?.region?.toUpperCase()

    // Validate invoice fields if enableInvoice is true
    if (params.enableInvoice && region === 'TW') {
      if (!params.taxId?.trim() || !params.eInvoiceCompanyName?.trim() || !params.eInvoiceCompanyAddress?.trim()) {
        dispatch(actions.app.toggleDialog('alertDialog', true, {
          title: 'Error',
          content: t('invoice.validation.requiredFields'),
        }))
        return
      }
    }

    // TH Tax Invoice：存檔前 trim 字串欄位，並於欄位下方顯示 inline error（FSD §13.8）
    if (params.enableInvoice && region === 'TH') {
      params.taxId = params.taxId?.trim()
      params.eInvoiceCompanyName = params.eInvoiceCompanyName?.trim()
      params.posId = params.posId?.trim()
      params.branchNo = params.branchNo?.trim()

      const invoiceErrors = {}
      if (!params.eInvoiceCompanyName) {
        invoiceErrors.eInvoiceCompanyName = t('invoice.validation.pleaseEnter')
      } else if (params.eInvoiceCompanyName.length > 80) {
        // Owner Name 長度需 <= 80（PMO 指示，DMP-46；與 InvoiceSettings 輸入上限一致）
        invoiceErrors.eInvoiceCompanyName = t('invoice.validation.maxLength80')
      } else if (!TH_TEXT_FIELD_PATTERN.test(params.eInvoiceCompanyName)) {
        // Owner Name 僅允許泰文 / 英文 / 數字 / 鍵盤符號
        invoiceErrors.eInvoiceCompanyName = t('invoice.validation.invalidChars')
      }
      if (!/^\d{13}$/.test(params.taxId ?? '')) {
        invoiceErrors.taxId = t('invoice.validation.taxId13')
      }
      // posId 選填，但若有填寫長度需 <= 60
      if (params.posId && params.posId.length > 60) {
        invoiceErrors.posId = t('invoice.validation.maxLength60')
      }
      // isHeadquarter=false 且 branchNo 為空 → 阻擋存檔
      if (params.isHeadquarter !== true && !params.branchNo) {
        invoiceErrors.branchNo = t('invoice.validation.pleaseEnter')
      } else if (params.branchNo && params.branchNo.length > 60) {
        // branchNo 有填寫時長度需 <= 60
        invoiceErrors.branchNo = t('invoice.validation.maxLength60')
      } else if (params.branchNo && !TH_TEXT_FIELD_PATTERN.test(params.branchNo)) {
        // branchNo 有填寫時僅允許泰文 / 英文 / 數字 / 鍵盤符號
        invoiceErrors.branchNo = t('invoice.validation.invalidChars')
      }
      if (Object.keys(invoiceErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...invoiceErrors }))
        return
      }
    }

    // 廚房單可選語言不可為空，否則 MR 端「廚房單格式」設定會無語言可選
    if (_.isEmpty(params.batchLocaleOptions)) {
      const message = t('merchant.validation.batchLocaleOptionsRequired')
      setErrors((prev) => ({ ...prev, batchLocaleOptions: message }))
      dispatch(actions.app.toggleDialog('alertDialog', true, {
        title: 'Error',
        content: `* ${message}`,
      }))
      return
    }

    if (params.geometry) {
      const [lat, lng] = params.geometry.split(',')
      params.lat = +_.trim(lat)
      params.lng = +_.trim(lng)
      delete params.geometry
    }
    try {
      if (params.account.username !== merchantState?.merchant?.shortCode) {
        await dispatch(actions.merchant.updateMerchantShortCode(merchantState.merchant.id, params.account.username))
      }
      if (params.account.password) {
        await dispatch(actions.merchant.updateMerchantPassword(merchantState.merchant.id, params.account.username, params.account.password))
      }
      delete params.account

      await dispatch(actions.merchant.updateMerchant(merchantState.merchant.id, params))
      dispatch(actions.merchant.getMerchants())
      handleClose()
    } catch (error) {
      if (error.code === 'RABBITPAY_API_KEY_NOT_SET') {
        // 同時更新 merchant.setting 的 toggle 和 isRabbitPayKeySet
        // 因為 useEffect 監聽 merchantState.merchant 變化後會重新同步 inputState
        // 若只更新 inputState 會被 useEffect 覆蓋
        setMerchant('merchant', {
          ...merchantState.merchant,
          setting: {
            ...merchantState.merchant?.setting,
            enableRabbitPay: false,
            enableDineInRabbitPay: false,
            isRabbitPayKeySet: false,
          },
        })
      }
      dispatch(actions.app.toggleDialog('alertDialog', true, { title: 'Error', content: error.message }))
    }
  }

  const handleDelete = async () => {
    dispatch(actions.app.toggleDialog('confirmDialog', null, {
      content: `Are you sure to delete merchant ${merchantState.merchant?.name}? This action is not reversible`,
      confirmCallback: async () => {
        try {
          await dispatch(actions.merchant.deleteMerchant(merchantState.merchant.id))
          dispatch(actions.merchant.getMerchants())
        } catch (error) {
          dispatch(actions.app.toggleDialog('alertDialog', true, { title: 'Error', content: error.message }))
        } finally {
          handleClose()
        }
      },
    }))
  }
  return (
    <InputStateContextProvider value={[inputState, updateInputState]}>
      <ErrorsStateContextProvider value={[errors, setErrors]}>
        <Dialog
          fullHeight
          title={`Edit ${merchantState.merchant?.name}`}
          open={open}
          handleClose={handleClose}
          renderActions={() => {
            return (
              <>
                <Button
                  variant='text'
                  color='secondary'
                  disableElevation
                  onClick={handleDelete}
                >
                  Delete
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  disableElevation
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
              </>
            )
          }}
        >
          <div className={classes.form}>
            <BasicSettings />
            <PaymentSettings />
            <OrderSettings />
            <PrinterSettings />
            <DirectorySettings />
            <AccountSettings />
            <ReportSettings />
            {merchantState.merchant?.region === 'TH' && <DatamartSettings />}
            {(merchantState.merchant?.region === 'TW' || merchantState.merchant?.region === 'TH') && <InvoiceSettings />}

          </div>
        </Dialog>
      </ErrorsStateContextProvider>
    </InputStateContextProvider>
  )
}

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
}))
